import {
  Box,
  Button,
  Chip,
  CircularProgress,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import Dayjs from 'dayjs'
import React from 'react'

import {
  fetchCashHqDeposits,
  fetchCashHqRollup,
  fetchCashLedger
} from 'api/cashRegister'
// Same (i) SOP button the procurement pages use; the Cash Register borrows it
// so Finance edits this SOP from the HQ -> SOP tab like every other one.
import SopButton from 'app/pages/Procurement/components/SopButton'

import { NoteTally } from './NoteCounter'
import Safe from './Safe'

// HQ view (hybrid layout, user 2026-08-26): the centre teams work in one
// ledger on their own page; HQ gets the shapes built for its questions —
// "where is cash sitting?" (roll-up, worst first), "does each challan match
// the bank statement?" (Deposits: one card per bank run), and — since
// 2026-09-02 — "what is in OUR drawer?" (The safe: envelopes on their way in,
// envelopes held, and what has left).
// Since 2026-08-27 a standalone page — sidebar Cash Register › HQ, moved out
// of the Procurement HQ tabs (user: "hq cash to be not in procurement").
const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  pageTitle: { fontWeight: 700 },
  pageSub: { color: '#777', fontSize: 13, marginBottom: theme.spacing(1) },
  subtabs: { marginBottom: theme.spacing(1.5) },
  amount: { fontVariantNumeric: 'tabular-nums' },
  bookedAmount: { fontVariantNumeric: 'tabular-nums', color: '#8a8175' },
  card: {
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5)
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: theme.spacing(2)
  },
  cardMeta: { color: '#777', fontSize: 13, marginTop: 2 },
  entryLine: { fontSize: 13, color: '#555' },
  detailCell: { background: '#fafafa', paddingTop: 0, paddingBottom: 12 },
  detailTable: { marginTop: 4 }
}))

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`

// LOCAL PREVIEW ONLY (vite dev server; never true in any build): synthetic
// data on fetch failure until the endpoints reach the dev API.
const SAMPLE_ROLLUP = {
  start_date: '2026-08-01',
  escalation_days: 3,
  results: [
    {
      centre: 'r1',
      name: 'Indiranagar',
      in_hand_count: 4,
      in_hand_amount: 46420,
      booked_amount: 58420,
      expected_balance: 500,
      oldest_days: 6,
      deposited_month_amount: 142300,
      uncounted_count: 1,
      note_tally: {
        rows: [
          { denomination: 500, count: 68, value: 34000 },
          { denomination: 200, count: 2, value: 400 },
          { denomination: 100, count: 2, value: 200 },
          { denomination: 20, count: 1, value: 20 }
        ],
        total: 34620
      }
    },
    {
      centre: 'r2',
      name: 'HSR Layout',
      in_hand_count: 2,
      in_hand_amount: 27880,
      booked_amount: 27880,
      expected_balance: 0,
      oldest_days: 2,
      deposited_month_amount: 64150,
      uncounted_count: 0,
      note_tally: {
        rows: [
          { denomination: 500, count: 55, value: 27500 },
          { denomination: 200, count: 1, value: 200 },
          { denomination: 100, count: 1, value: 100 },
          { denomination: 50, count: 1, value: 50 },
          { denomination: 20, count: 1, value: 20 },
          { denomination: 10, count: 1, value: 10 }
        ],
        total: 27880
      }
    },
    {
      centre: 'r3',
      name: 'Sahakar Nagar',
      in_hand_count: 0,
      in_hand_amount: 0,
      booked_amount: 0,
      expected_balance: 0,
      oldest_days: 0,
      deposited_month_amount: 121000,
      uncounted_count: 0,
      note_tally: { rows: [] }
    }
  ]
}
// In-hand detail rows per centre for the local preview (keyed by the sample
// roll-up's centre ids).
const SAMPLE_DETAIL = {
  r1: [
    {
      uuid: 's1',
      collected_on: '2026-08-26',
      payer: 'Ridhima S · After School',
      parent_phone: '+91 98300 11223',
      booked_amount: 18540,
      amount_received: 18540,
      amount: 18540,
      variance: 0,
      recorded: false,
      reason_label: '',
      note: '',
      mode: 'cash',
      collected_by: 'Meenakshi Ghosh',
      age_days: 0
    },
    {
      uuid: 's2',
      collected_on: '2026-08-25',
      payer: 'Aarav B · Playdate',
      parent_phone: '+91 98450 44556',
      booked_amount: 164967,
      amount_received: 15000,
      amount: 15000,
      variance: -149967,
      recorded: true,
      reason_label: 'Course or plan changed — differential only',
      note: 'Preschool timing extension',
      mode: 'cash',
      collected_by: 'Meenakshi Ghosh',
      age_days: 1
    },
    {
      uuid: 's3',
      collected_on: '2026-08-22',
      payer: 'Zoya K · After School',
      parent_phone: '+91 99020 77889',
      booked_amount: 12880,
      amount_received: 12880,
      amount: 12880,
      variance: 0,
      recorded: false,
      reason_label: '',
      note: '',
      mode: 'cheque',
      collected_by: 'Swagata Maiti',
      age_days: 4
    },
    {
      uuid: 's4',
      collected_on: '2026-08-20',
      payer: 'Vihaan R · Summer Camp',
      parent_phone: '+91 96860 33445',
      booked_amount: 12000,
      amount_received: 12000,
      amount: 12000,
      variance: 0,
      recorded: false,
      reason_label: '',
      note: '',
      mode: 'cash',
      collected_by: 'Sanhita Choudhury',
      age_days: 6
    }
  ],
  r2: [
    {
      uuid: 's7',
      collected_on: '2026-08-25',
      payer: 'Ishaan M · After School',
      parent_phone: '+91 98867 55443',
      booked_amount: 16880,
      amount_received: 16880,
      amount: 16880,
      variance: 0,
      recorded: false,
      reason_label: '',
      note: '',
      mode: 'cash',
      collected_by: 'Divya R',
      age_days: 1
    },
    {
      uuid: 's8',
      collected_on: '2026-08-24',
      payer: 'Anaya P · Playdate',
      parent_phone: '+91 96322 11009',
      booked_amount: 11000,
      amount_received: 11000,
      amount: 11000,
      variance: 0,
      recorded: false,
      reason_label: '',
      note: '',
      mode: 'cash',
      collected_by: 'Divya R',
      age_days: 2
    }
  ]
}

const SAMPLE_DEPOSITS = {
  count: 2,
  results: [
    {
      uuid: 'sd1',
      centre: { uuid: 'r1', name: 'Indiranagar' },
      route: 'bank',
      destination: 'HDFC Indiranagar',
      bank: 'HDFC Indiranagar',
      handed_to: '',
      deposited_on: '2026-08-16',
      deposited_by: 'Sanhita Choudhury',
      amount: 21300,
      note: '',
      entries: [
        {
          payer: 'Myra D · After School',
          parent_phone: '+91 90080 99001',
          amount: 21300,
          mode: 'cash',
          collected_on: '2026-08-14'
        }
      ],
      challans: [{ uuid: 'sc1', name: 'slip.jpg', url: '#' }]
    },
    {
      uuid: 'sd2',
      centre: { uuid: 'r3', name: 'Sahakar Nagar' },
      route: 'handover',
      destination: 'Handed to Nehasri',
      bank: '',
      handed_to: 'Nehasri',
      deposited_on: '2026-08-12',
      deposited_by: 'Meenakshi Ghosh',
      amount: 121000,
      note: 'two bundles',
      entries: [
        {
          payer: 'Kabir J · After School',
          parent_phone: '+91 97400 22110',
          amount: 121000,
          mode: 'cash',
          collected_on: '2026-08-11'
        }
      ],
      challans: [{ uuid: 'sc2', name: 'challan_0811.pdf', url: '#' }]
    }
  ]
}

const AGE_CHIP = (days, threshold) => {
  if (!days) return null
  const late = days >= threshold
  return (
    <Chip
      size="small"
      label={`oldest ${days}d`}
      style={
        late
          ? { background: '#ffebee', color: '#c62828' }
          : { background: '#fff8e1', color: '#8d6e00' }
      }
    />
  )
}

const Rollup = ({ classes }) => {
  const [loading, setLoading] = React.useState(true)
  const [data, setData] = React.useState(null)
  const [error, setError] = React.useState('')
  // Click a centre row → its full in-hand list unfolds underneath (user
  // 2026-08-27). Details are fetched once per centre and cached.
  const [expanded, setExpanded] = React.useState('')
  const [details, setDetails] = React.useState({})

  const toggleExpand = (r) => {
    if (!r.centre || !r.in_hand_count) return
    const centreId = r.centre
    if (expanded === centreId) {
      setExpanded('')
      return
    }
    setExpanded(centreId)
    if (details[centreId]) return
    setDetails((prev) => ({ ...prev, [centreId]: { loading: true, rows: [] } }))
    fetchCashLedger(centreId)
      .then((res) => {
        const rows = (res?.results || []).filter(
          (row) => row.status === 'in_hand'
        )
        setDetails((prev) => ({
          ...prev,
          [centreId]: { loading: false, rows }
        }))
      })
      .catch(() => {
        // LOCAL PREVIEW ONLY: synthetic detail until the endpoint reaches dev.
        const rows = import.meta.env.DEV ? SAMPLE_DETAIL[centreId] || [] : []
        setDetails((prev) => ({
          ...prev,
          [centreId]: { loading: false, rows }
        }))
      })
  }

  const load = React.useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await fetchCashHqRollup())
    } catch (e) {
      if (import.meta.env.DEV) {
        setData(SAMPLE_ROLLUP)
      } else {
        setError('Could not load the roll-up. Please retry.')
      }
    } finally {
      setLoading(false)
    }
  }, [])
  React.useEffect(() => {
    load()
  }, [load])

  if (loading)
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  if (error)
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button size="small" onClick={load} style={{ marginTop: 8 }}>
          Retry
        </Button>
      </Box>
    )

  const rows = data?.results || []
  const threshold = Number(data?.escalation_days || 3)
  const totalInHand = rows.reduce(
    (sum, r) => sum + Number(r.in_hand_amount || 0),
    0
  )
  return (
    <>
      <Typography variant="body2" color="textSecondary" gutterBottom>
        {inr(totalInHand)} sitting across{' '}
        {rows.filter((r) => r.in_hand_count > 0).length} centre(s) — worst
        first. Register counts collections from{' '}
        {Dayjs(data?.start_date).format('D MMM YYYY')}. Click a centre to see
        every collection it is holding.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Centre</TableCell>
            <TableCell align="right">In hand</TableCell>
            <TableCell align="right">Booked</TableCell>
            <TableCell align="right">Entries</TableCell>
            <TableCell>Notes counted</TableCell>
            <TableCell>Ageing</TableCell>
            <TableCell align="right">
              Deposited · {Dayjs().format('MMM')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => {
            const detail = details[r.centre]
            const isOpen = expanded === r.centre && !!r.centre
            return (
              <React.Fragment key={r.centre || r.name}>
                <TableRow
                  hover
                  onClick={() => toggleExpand(r)}
                  style={
                    r.centre && r.in_hand_count ? { cursor: 'pointer' } : {}
                  }
                >
                  <TableCell>
                    {r.centre && r.in_hand_count ? (isOpen ? '▾ ' : '▸ ') : ''}
                    {r.name}
                  </TableCell>
                  <TableCell align="right" className={classes.amount}>
                    {r.in_hand_count ? inr(r.in_hand_amount) : '—'}
                    {/* Money parents still owe on part-paid collections —
                        nobody is holding it, so it sits beside the cash total
                        rather than inside it. */}
                    {r.expected_balance > 0 && (
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                      >
                        {inr(r.expected_balance)} still due
                      </Typography>
                    )}
                  </TableCell>
                  {/* What the invoices add up to. Shown next to the cash so a
                      gap is visible rather than quietly corrected away — one
                      Sahakar Nagar row alone booked ₹1.65L against ₹30,000
                      actually received. */}
                  <TableCell align="right" className={classes.bookedAmount}>
                    {r.in_hand_count && r.booked_amount ? (
                      <>
                        {inr(r.booked_amount)}
                        {Math.abs(
                          Number(r.booked_amount || 0) -
                            Number(r.in_hand_amount || 0)
                        ) >= 1 && (
                          <Typography
                            variant="caption"
                            display="block"
                            style={{ color: '#c0453f' }}
                          >
                            {inr(
                              Number(r.booked_amount || 0) -
                                Number(r.in_hand_amount || 0)
                            )}{' '}
                            explained
                          </Typography>
                        )}
                      </>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                  <TableCell align="right">{r.in_hand_count || '—'}</TableCell>
                  {/* Cash cannot be settled until it is counted, so an
                      uncounted pile is a blockage HQ should see here rather
                      than discover when a bank run bounces. */}
                  <TableCell>
                    {r.uncounted_count > 0 ? (
                      <Chip
                        size="small"
                        label={`${r.uncounted_count} not counted`}
                        style={{ background: '#fdecea', color: '#a32b21' }}
                      />
                    ) : (
                      <NoteTally rows={r.note_tally?.rows || []} />
                    )}
                  </TableCell>
                  <TableCell>
                    {AGE_CHIP(r.oldest_days, threshold) || (
                      <Chip
                        size="small"
                        label="clear"
                        style={{ background: '#e8f5e9', color: '#2e7d32' }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.amount}>
                    {r.deposited_month_amount
                      ? inr(r.deposited_month_amount)
                      : '—'}
                  </TableCell>
                </TableRow>
                {isOpen && (
                  <TableRow>
                    <TableCell colSpan={7} className={classes.detailCell}>
                      {!detail || detail.loading ? (
                        <Box display="flex" justifyContent="center" p={2}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : detail.rows.length ? (
                        <Table size="small" className={classes.detailTable}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Collected</TableCell>
                              <TableCell>Student / Order</TableCell>
                              <TableCell>Parent phone</TableCell>
                              <TableCell align="right">Booked</TableCell>
                              <TableCell align="right">Received</TableCell>
                              <TableCell>Why different</TableCell>
                              <TableCell>Mode</TableCell>
                              <TableCell>Collected by</TableCell>
                              <TableCell>Age</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {detail.rows.map((row) => (
                              <TableRow key={row.uuid}>
                                <TableCell>
                                  {Dayjs(row.collected_on).format('D MMM')}
                                </TableCell>
                                <TableCell>{row.payer}</TableCell>
                                <TableCell>{row.parent_phone || '—'}</TableCell>
                                <TableCell
                                  align="right"
                                  className={classes.bookedAmount}
                                >
                                  {inr(row.booked_amount ?? row.amount)}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  className={classes.amount}
                                >
                                  {inr(row.amount_received ?? row.amount)}
                                </TableCell>
                                <TableCell>
                                  {row.variance ? (
                                    <Tooltip title={row.note || ''}>
                                      <Chip
                                        size="small"
                                        label={row.reason_label || 'Difference'}
                                        style={{
                                          background: '#fdece7',
                                          color: '#c0453f'
                                        }}
                                      />
                                    </Tooltip>
                                  ) : row.recorded ? (
                                    '—'
                                  ) : (
                                    <Typography
                                      variant="caption"
                                      color="textSecondary"
                                    >
                                      Not checked
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell
                                  style={{ textTransform: 'capitalize' }}
                                >
                                  {row.mode}
                                </TableCell>
                                <TableCell>{row.collected_by || '—'}</TableCell>
                                <TableCell>
                                  {AGE_CHIP(row.age_days, threshold) || '0d'}
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          Nothing in hand right now.
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            )
          })}
          {!rows.length && (
            <TableRow>
              <TableCell colSpan={7}>
                <Typography variant="body2" color="textSecondary">
                  No cash in hand and nothing deposited this month.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </>
  )
}

const Deposits = ({ classes }) => {
  const [loading, setLoading] = React.useState(true)
  const [data, setData] = React.useState(null)
  const [error, setError] = React.useState('')
  const [centre, setCentre] = React.useState('all')
  const [centreOptions, setCentreOptions] = React.useState([])

  const load = React.useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const res = await fetchCashHqDeposits(
        centre && centre !== 'all' ? { centre, limit: 50 } : { limit: 50 }
      )
      setData(res)
      // The filter offers only centres that actually have deposits, learned
      // from the unfiltered list — no second endpoint needed.
      if (centre === 'all') {
        const seen = new Map()
        ;(res?.results || []).forEach((d) =>
          seen.set(d.centre.uuid, d.centre.name)
        )
        setCentreOptions(
          [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]))
        )
      }
    } catch (e) {
      if (import.meta.env.DEV) {
        setData(SAMPLE_DEPOSITS)
      } else {
        setError('Could not load deposits. Please retry.')
      }
    } finally {
      setLoading(false)
    }
  }, [centre])
  React.useEffect(() => {
    load()
  }, [load])

  if (error)
    return (
      <Box p={3}>
        <Typography color="error">{error}</Typography>
        <Button size="small" onClick={load} style={{ marginTop: 8 }}>
          Retry
        </Button>
      </Box>
    )

  const deposits = data?.results || []
  return (
    <>
      <Box display="flex" alignItems="center" mb={1.5} style={{ gap: 12 }}>
        <TextField
          select
          size="small"
          variant="outlined"
          label="Centre"
          value={centre}
          onChange={(e) => setCentre(e.target.value)}
          style={{ minWidth: 220 }}
        >
          <MenuItem value="all">All centres</MenuItem>
          {centreOptions.map(([uuid, name]) => (
            <MenuItem key={uuid} value={uuid}>
              {name}
            </MenuItem>
          ))}
        </TextField>
        {!loading && (
          <Typography variant="body2" color="textSecondary">
            {data?.count || 0} settlement(s) — one card each, matching one line
            on the bank statement
          </Typography>
        )}
      </Box>
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        deposits.map((d) => (
          <Paper key={d.uuid} variant="outlined" className={classes.card}>
            <div className={classes.cardTop}>
              <Typography variant="body1">
                <b>{Dayjs(d.deposited_on).format('D MMM')}</b> · {d.centre.name}{' '}
                · {d.destination || d.bank}
              </Typography>
              <Typography variant="body1" className={classes.amount}>
                <b>{inr(d.amount)}</b>
              </Typography>
            </div>
            <div className={classes.cardMeta}>
              {d.entries.length} collection(s) · recorded by{' '}
              {d.deposited_by || '—'}
              {d.challans.map((c) => (
                <React.Fragment key={c.uuid}>
                  {' · '}
                  <a
                    href={c.view_url || c.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {c.name}
                  </a>
                </React.Fragment>
              ))}
              {d.note ? ` · ${d.note}` : ''}
            </div>
            <Box mt={0.5}>
              {d.entries.map((e, i) => (
                <div key={i} className={classes.entryLine}>
                  {Dayjs(e.collected_on).format('D MMM')} · {e.payer}
                  {e.parent_phone ? ` · ${e.parent_phone}` : ''} ·{' '}
                  {inr(e.amount)} ({e.mode})
                </div>
              ))}
            </Box>
          </Paper>
        ))
      )}
      {!loading && !deposits.length && (
        <Typography variant="body2" color="textSecondary">
          Nothing has left a centre yet — no bank runs, handovers or transfers
          to HQ.
        </Typography>
      )}
    </>
  )
}

const CashRegisterHqPage = () => {
  const classes = useStyles()
  const [view, setView] = React.useState('rollup')
  return (
    <Box className={classes.root}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
        <Typography variant="h6" className={classes.pageTitle}>
          Cash Register — HQ
        </Typography>
        <SopButton category="cash_register" />
      </div>
      <Typography className={classes.pageSub}>
        Cash in hand across centres, what HQ is holding, and every deposit
        challan by challan.
      </Typography>
      <Tabs
        className={classes.subtabs}
        value={view}
        onChange={(e, v) => setView(v)}
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab value="rollup" label="Cash sitting at centres" />
        <Tab value="safe" label="HQ safe" />
        <Tab value="deposits" label="What the centres sent out" />
      </Tabs>
      {view === 'rollup' && <Rollup classes={classes} />}
      {view === 'safe' && <Safe />}
      {view === 'deposits' && <Deposits classes={classes} />}
    </Box>
  )
}

export default CashRegisterHqPage
